<template>
    <Transition name="fade">
        <PermissionDialog :is-open="showMicPermissionAide" @cancel="handleDialogCancel" />
    </Transition>
    <RecordingTestDialog @dismiss="usingText = true" @mic-selected="handleMicSelection" />
    <div class="w-full flex flex-col gap-6 md:gap-10">
        <div v-if="!isAnswered" class="flex flex-col gap-3 max-w-[440px] mx-auto text-center">
            <p class="text-valence-grey-750 body-small">{{ currentQuestion.title }}</p>
            <h2 class="title-subsection font-medium! text-[#262626] leading-[normal]">{{ currentQuestion.text }}</h2>
        </div>
        <ChatNotification v-if="error" class="md:max-w-[800px] mx-auto" :message="error.message" theme="error" @dismiss="$emit('dismiss-error')" />
        <div
            class="md:max-w-[800px] w-full mx-auto grow bg-white flex md:flex-row flex-wrap flex-col items-start md:items-center gap-4 md:gap-1 justify-between rounded-3xl px-6 transition-color"
            :class="[isRecording ? 'py-5' : 'py-6', isAnswered && !isLoading ? 'gradient-border' : 'border-[#E8E8E8] border-2']">
            <template v-if="showLoader">
                <div class="text-left h-10 body-small gradient-text flex items-center gap-3">Thinking... <LoadingSpinner class="h-4 text-valence-grey-750" /></div>
            </template>
            <template v-else-if="isAnswered">
                <div class="grow flex flex-col gap-10 justify-between">
                    <div class="grow title-body text-[#262626]">
                        <template v-if="currentQuestion.offTopic"> Let’s get back on track and discuss topics about your role, your position, and your team at work. </template>
                        <template v-else>
                            {{ currentQuestion.llmResponse }}
                        </template>
                    </div>
                    <div class="body-small text-valence-grey-750 flex flex-row-reverse md:flex-row items-center justify-between">
                        <div class="hidden md:block">Just to make sure - did I get this right?</div>
                        <button type="button" class="transition-colors hover:text--primary-500 flex gap-1 items-center" @click="handleMoreContext">
                            <i v-if="showTextInput" class="bi bi-pencil text-xs" aria-hidden="true" />
                            <span v-else class="h-8 w-8">
                                <MicrophoneCircle background-class="fill-transparent" foreground-class="stroke-[currentColor]" />
                            </span>
                            <template v-if="currentQuestion.offTopic"> Edit response </template>
                            <template v-else> Add more context </template>
                        </button>
                    </div>
                </div>
            </template>

            <template v-else>
                <div class="grow w-full md:w-auto">
                    <BaseTextarea
                        v-if="showTextInput"
                        ref="baseTextareaRef"
                        v-model:input="text"
                        placeholder="Type your answer here"
                        :disabled="disabled"
                        class="caret-valence-primary-500 body text-[#262626]"
                        aria-label="Answer"
                        @keydown.enter.prevent="handleText"></BaseTextarea>
                    <div v-else class="w-full flex items-center gap-6">
                        <div class="w-full md:w-4/5">
                            <VoiceLevels :class="{ 'opacity-40': !isRecording }" class="py-4" :bar-size="isRecording ? BAR_SIZE.SMALL : BAR_SIZE.TINY" :levels="levelHistory" />
                        </div>
                        <div v-if="isRecording" class="text-valence-grey-750 body-small">
                            <template v-if="isSilent"> No sound detected </template>
                            <template v-else> Listening... </template>
                        </div>
                    </div>
                </div>
                <!-- New mode: mic shrinks when text is present; send grows from 0 -->
                <div v-if="betterVoiceComposerEnabled" class="self-end flex items-center gap-2">
                    <template v-if="isRecording">
                        <button
                            data-testid="record-cancel"
                            type="button"
                            title="Cancel"
                            class="text-valence-grey-750 hover:text-[#5E5E5E] h-10 w-10 flex items-center justify-center"
                            @click="cancelRecording">
                            <i class="bi bi-x-lg text-sm" aria-hidden="true" />
                        </button>
                        <button
                            type="button"
                            data-testid="record-stop"
                            title="Send recording"
                            class="shrink-0 flex items-center justify-center h-10 w-10 rounded-full bg-valence-primary-500 hover:bg-[#4B508F] text-white"
                            @click="stopRecording">
                            <i class="bi bi-send-fill text-lg rotate-45" aria-hidden="true" />
                        </button>
                    </template>
                    <template v-else>
                        <button
                            v-if="voiceInputEnabled"
                            :disabled="disabled"
                            type="button"
                            aria-label="Record voice message"
                            title="Record voice message"
                            data-testid="record-voice"
                            class="shrink-0 flex items-center justify-center rounded-full bg-valence-primary-500 hover:bg-[#4B508F] text-white transition-[width,height,background-color,opacity] duration-300 ease-out disabled:opacity-50"
                            :class="text ? 'h-10 w-10' : 'h-12 w-12'"
                            @click="handleMicStart">
                            <i class="bi bi-mic-fill transition-[font-size] duration-300" :class="text ? 'text-lg' : 'text-2xl'" aria-hidden="true" />
                        </button>
                        <button
                            :disabled="!text || disabled"
                            type="button"
                            data-testid="text-submit"
                            title="Submit"
                            class="shrink-0 flex items-center justify-center rounded-full bg-valence-primary-500 hover:bg-[#4B508F] text-white transition-[width,height,opacity,transform,background-color] duration-300 ease-out overflow-hidden disabled:opacity-50"
                            :class="submitSizeClass"
                            @click="handleText">
                            <i class="bi bi-send-fill text-base rotate-45" aria-hidden="true" />
                        </button>
                    </template>
                </div>
                <!-- Old mode: existing toggle + button layout -->
                <div v-else class="self-end transition-color flex md:justify-between items-center" :class="[hasAnswer ? 'gap-3' : 'gap-6', showTextInput ? 'self-end' : '']">
                    <template v-if="showTextInput">
                        <button
                            v-if="voiceInputEnabled"
                            data-testid="voice-toggle"
                            type="button"
                            class="hover:text-[#4B508F] text-valence-primary-500 body-small flex gap-1 items-center"
                            @click="usingText = false">
                            <span class="h-8 w-8">
                                <MicrophoneCircle background-class="fill-transparent" foreground-class="stroke-[currentColor]" />
                            </span>
                            <template v-if="!text"><span class="hidden md:inline">Switch to voice</span><span class="md:hidden">Voice</span></template>
                        </button>
                        <button
                            data-testid="text-submit"
                            title="Submit"
                            :disabled="!text || disabled"
                            type="button"
                            class="group/button text-valence-primary-500 h-10 w-10"
                            @click="handleText">
                            <ArrowCircle
                                direction="up"
                                background-class="group-disabled/button:opacity-[.24] group-hover/button:md:fill-[#4B508F] fill-valence-primary-500"
                                foreground-class="group-disabled/button:fill-valence-primary-500 group-disabled/button:stroke-valence-primary-500 stroke-white fill-white" />
                        </button>
                    </template>
                    <template v-else>
                        <template v-if="isRecording">
                            <button data-testid="record-cancel" type="button" title="Cancel" class="text-valence-grey-750 hover:text-[#5E5E5E]" @click="cancelRecording">
                                <i class="bi bi-x-lg text-sm" aria-hidden="true" />
                            </button>
                            <button
                                type="button"
                                data-testid="record-stop"
                                class="hover:bg-[#a70d19] bg-[#CF1322] hover:ring-[#a70d19] text-white ring-[#CF1322] rounded-3xl flex items-center gap-1 body-small py-2 px-6"
                                @click="stopRecording">
                                <span class="h-6 w-6">
                                    <StopCircle background-class="fill-transparent" foreground-class="fill-[currentColor]" />
                                </span>
                                Stop
                            </button>
                        </template>
                        <template v-else>
                            <button
                                data-testid="text-toggle"
                                type="button"
                                :disabled="isRecording"
                                class="hover:text-[#4B508F] text-valence-primary-500 body-small flex gap-3 items-center disabled:text-valence-grey-750"
                                @click="usingText = true">
                                <span class="h-4 w-4">
                                    <Keyboard foreground-class="fill-[currentColor]" />
                                </span>
                                <template v-if="!isRecording"><span class="hidden md:inline">Switch to text</span><span class="md:hidden">Text</span></template>
                            </button>
                            <button
                                data-testid="record-start"
                                type="button"
                                class="bg-valence-primary-500 hover:bg-[#4B508F] hover:ring-[#4B508F] text-white ring-valence-primary-500 rounded-3xl flex items-center gap-1 body-small py-2 px-6"
                                @click="startRecording">
                                <span class="h-6 w-6">
                                    <MicrophoneCircle background-class="fill-transparent" foreground-class="stroke-[currentColor]" />
                                </span>
                                Talk
                            </button>
                        </template>
                    </template>
                </div>
            </template>
        </div>

        <Transition name="fade">
            <div v-if="isAnswered && !currentQuestion.offTopic" class="inline-block mx-auto">
                <button type="button" :disabled="isLoading" class="button button-primary" @click="handleNextQuestion">Continue</button>
            </div>
            <template v-else-if="!isAnswered && !isLoading && !error">
                <ContextMeterText v-if="showTextInput && hasAnswer" :text="text" />
                <ContextMeter v-else-if="isRecording" :running="contextMeterRunning" />
            </template>
        </Transition>
    </div>
</template>

<script setup>
import { AudioRecorder } from "/js/AudioRecorder.js";
import { transcribeRecording } from "/js/transcription.js";
import { useThrottleFn } from "@vueuse/core";
import ChatNotification from "~vue/ChatNotification.vue";
import BaseTextarea from "~vue/components/form/BaseTextarea.vue";
import VoiceLevels, { BAR_SIZE } from "~vue/components/VoiceLevels.vue";
import { useMicConsent } from "~vue/composables/useMicConsent";
import { CHAT_EVENT } from "~vue/events";
import ArrowCircle from "~vue/icons/ArrowCircle.vue";
import Keyboard from "~vue/icons/Keyboard.vue";
import LoadingSpinner from "~vue/icons/LoadingSpinner.vue";
import MicrophoneCircle from "~vue/icons/MicrophoneCircle.vue";
import StopCircle from "~vue/icons/StopCircle.vue";
import ContextMeter from "~vue/Onboarding/ContextMeter.vue";
import ContextMeterText from "~vue/Onboarding/ContextMeterText.vue";
import RecordingTestDialog from "~vue/RecordingTestDialog.vue";
import { isInIFrame } from "~vue/utils";
import { logError, logUserInteraction } from "~vue/utils/logUtils";
import { computed, inject, nextTick, onMounted, onUnmounted, ref, useTemplateRef, watch } from "vue";

import PermissionDialog from "./PermissionDialog.vue";

const SILENCE_TIMEOUT_MS = 5000;

const props = defineProps({
    error: { type: Error, default: null },
    disabled: { type: Boolean, default: false },
    currentQuestion: { type: Object, required: true },
    isLoading: { type: Boolean, default: false },
    betterVoiceComposerEnabled: { type: Boolean, default: false },
    hideChatVoiceInputEnabled: { type: Boolean, default: false },
});

const emit = defineEmits(["answer", "dismiss-error", "next", "transcribe-error"]);

const { emitter } = inject("globalProperties");
const organizationDetails = inject("organization_details", null);
const { micConsentBlocks } = useMicConsent();

const STATE = {
    QUESTION: "question",
    RECORDING: "recording",
    TRANSCRIBING: "transcribing",
    ANSWER: "answer",
};

const baseTextareaRef = useTemplateRef("baseTextareaRef");
const contextMeterRunning = ref(false);
const recordingLevelSamplingRate = ref(100);
const text = ref("");
const voiceInputEnabled = computed(() => !props.hideChatVoiceInputEnabled && !organizationDetails?.value?.is_chat_voice_input_hidden);

// Default to text mode when the new voice composer is enabled (mic button starts recording directly)
const usingText = ref(props.betterVoiceComposerEnabled || !voiceInputEnabled.value);

// With no mic there is nothing to switch back from, so the question always answers by text.
const showTextInput = computed(() => usingText.value || !voiceInputEnabled.value);
const state = ref(STATE.QUESTION);
const showMicPermissionAide = ref(false);
const recorder = ref(new AudioRecorder({ levelCheckIntervalMs: 100 }));
const levelHistory = ref([]);
const silenceTimeout = ref(null);

const isRecording = computed(() => state.value === STATE.RECORDING);
const isTranscribing = computed(() => state.value === STATE.TRANSCRIBING);
const isAnswered = computed(() => state.value === STATE.ANSWER);
const showLoader = computed(() => isTranscribing.value || props.isLoading);
const isSilent = computed(() => silenceTimeout.value !== null);
const isProcessing = computed(() => isRecording.value || isTranscribing.value);
const shouldAppend = computed(() => !!props.currentQuestion.answer && !props.currentQuestion.offTopic);

// The submit button normally grows out of zero width as the mic shrinks. With no mic to trade
// places with, it holds the mic's resting size instead.
const submitSizeClass = computed(() => {
    if (!voiceInputEnabled.value) {
        return "h-12 w-12 opacity-100 scale-100";
    }
    return text.value ? "h-10 w-10 opacity-100 scale-100" : "h-0 w-0 opacity-0 scale-0";
});

const hasAnswer = computed(() => {
    if (showTextInput.value) {
        return text.value.length > 0;
    }

    return isRecording.value;
});

watch(usingText, (value) => {
    if (!value) {
        return;
    }

    nextTick(focusTextInput);
});

watch(isRecording, (value) => {
    if (value) {
        emit("dismiss-error");
    }
});

watch(
    () => props.currentQuestion,
    () => {
        state.value = STATE.QUESTION;
        text.value = "";
    },
);

watch(state, (value) => {
    if (value === STATE.QUESTION && usingText.value) {
        nextTick(focusTextInput);
    }
});

watch(
    () => props.error,
    (value) => {
        if (value) {
            handleErrorFallback();
        }
    },
);

onMounted(() => {
    recorder.value.on("level", handleRecorderLevel);
    recorder.value.on("finish", (data) => handleRecorderFinished(data));
    recorder.value.on("start", handleRecorderStart);
    recorder.value.on("stop", handleRecorderStop);
    recorder.value.on("silence", handleSilence);
});

onUnmounted(() => {
    if (recorder.value) {
        clearRecorder();
    }
});

function focusTextInput() {
    baseTextareaRef.value?.textareaRef?.focus();
}

function handleErrorFallback() {
    state.value = STATE.QUESTION;
    usingText.value = true;
}

function handleDialogCancel() {
    showMicPermissionAide.value = false;
    usingText.value = true;
    logUserInteraction("use_text", {});
}

async function startRecording() {
    if (!voiceInputEnabled.value) {
        return;
    }

    if (micConsentBlocks(startRecording)) {
        return;
    }

    try {
        let viewedPrompt = false;
        await recorder.value.start((micPermissionState) => {
            if (micPermissionState === "prompt" || isInIFrame()) {
                showMicPermissionAide.value = true;
                viewedPrompt = true;
            }
        });

        /*
         *Reset any draft text that may have existed.before switching
         * to voice
         */
        if (text.value) {
            text.value = "";
        }

        if (viewedPrompt) {
            // User allowed mic after prompt if it doesn't error above
            logUserInteraction("allowed_mic", {});

            /*
             * User may have chosen "I'd rather type" in the permission dialog
             * but still grant permission in the browser prompt. In this case,
             * we should not continue with a recording because the user
             * chose to use text.
             */
            if (usingText.value) {
                recorder.value.stop({ abort: true });
                return;
            }
        }
    } catch {
        usingText.value = true;
        openTestDialog();
        logUserInteraction("use_text", {});
    } finally {
        if (showMicPermissionAide.value) {
            showMicPermissionAide.value = false;
        }
    }
}

function clearRecorder() {
    recorder.value.off("level", handleRecorderLevel);
    recorder.value.off("finish", (data) => handleRecorderFinished(data));
    recorder.value.off("start", handleRecorderStart);
    recorder.value.off("stop", handleRecorderStop);
    recorder.value.off("silence", handleSilence);
}

function stopRecording() {
    recorder.value.stop();
}

function cancelRecording() {
    recorder.value.stop({ abort: true });
    levelHistory.value = [];
    state.value = STATE.QUESTION;
    if (props.betterVoiceComposerEnabled) {
        // In the new composer, return to text mode so the textarea + mic+send buttons reappear
        usingText.value = true;
    }
}

async function handleMicStart() {
    usingText.value = false;
    await startRecording();
}

function handleText() {
    emit("answer", {
        answer: text.value,
        append: shouldAppend,
        onFinish: () => {
            state.value = STATE.ANSWER;
        },
        usingMic: false,
    });
}

function handleRecorderStart() {
    state.value = STATE.RECORDING;
}

async function handleRecorderFinished({ blob }) {
    levelHistory.value = [];
    state.value = STATE.TRANSCRIBING;

    try {
        const transcript = await transcribeRecording({
            blob,
        });
        /*
         * Persist transcript as text answer in case there is an upstream
         * error when submitting answer.
         */
        text.value = transcript;

        emit("answer", {
            answer: transcript,
            append: shouldAppend,
            onFinish: () => {
                state.value = STATE.ANSWER;
            },
            usingMic: true,
        });
    } catch (e) {
        const error = new Error("I am having trouble listening to you. Please try again.");
        emit("transcribe-error", error);
        logError(e);
    }
}

function openTestDialog() {
    emitter.emit(CHAT_EVENT.OPEN_MIC_TEST_DIALOG);
}

function clearSilenceTimeout() {
    if (silenceTimeout.value) {
        window.clearTimeout(silenceTimeout.value);
        silenceTimeout.value = null;
    }
}

function handleRecorderStop() {
    contextMeterRunning.value = false;
    clearSilenceTimeout();
}

function handleSilence() {
    silenceTimeout.value = window.setTimeout(() => {
        cancelRecording();
        openTestDialog();
    }, SILENCE_TIMEOUT_MS);
}

function handleNextQuestion() {
    emit("next");
}

function handleMoreContext() {
    if (showTextInput.value) {
        text.value = "";
        state.value = STATE.QUESTION;
    } else {
        startRecording();
    }

    logUserInteraction("give_more_context_clicked", {});
}

const updateContextMeterSampling = useThrottleFn(({ isBelowSilenceThreshold }) => {
    /*
     * Check whether user is silent. Context meter should only run
     * when we detect signal.
     * If silence is detected, then the sampling rate is
     * increased so that signal is detected sooner. If signal is detected,
     * the sampling rate is decreased to accomodate natural pausing.
     */
    contextMeterRunning.value = !isBelowSilenceThreshold;
    if (isBelowSilenceThreshold) {
        recordingLevelSamplingRate.value = 200;
    } else {
        recordingLevelSamplingRate.value = 4000;
    }
}, recordingLevelSamplingRate);

function handleRecorderLevel(level) {
    const isBelowSilenceThreshold = recorder.value.isBelowSilenceThreshold(level);
    if (!isBelowSilenceThreshold) {
        clearSilenceTimeout();
    }
    levelHistory.value.push(level);

    updateContextMeterSampling({ isBelowSilenceThreshold });
}

function handleMicSelection(event) {
    recorder.value?.setAudioInputDeviceId(event.deviceId);
}

defineExpose({ handleMicSelection, cancelRecording, isProcessing, isAnswered });
</script>

<style scoped>
textarea {
    -ms-overflow-style: none;
    scrollbar-width: none;
}

textarea::-webkit-scrollbar,
textarea::-webkit-resizer {
    display: none;
}
</style>
